import React, { useEffect, useRef, useState } from 'react'
import { Button, Input } from '@yuanfudao/metis-web-component'
import { encryptStr, ERROR_CODE, ERROR_CODE_ZH } from '@yuanfudao/metis-unified-login'
import { sendVerifyCode } from '@yuanfudao/metis-unified-login/es/service/login'
import { formatPhone, phoneReg } from '@yuanfudao/metis-unified-login/es/utils/phone'
import lessModule from './index.module.less'

interface VerifyPhoneProps {
  onNext: (phone: string, verification: string) => Promise<void>
  onCancel: () => void
  initialPhone?: string
  retryAt: number
  onCodeSent: (retryAt: number) => void
}

export default function VerifyPhone({ onNext, onCancel, initialPhone = '', retryAt, onCodeSent }: VerifyPhoneProps) {
  const [phone, setPhone] = useState(initialPhone)
  const [verification, setVerification] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [sending, setSending] = useState(false)
  const [seconds, setSeconds] = useState(() => Math.max(0, Math.ceil((retryAt - Date.now()) / 1000)))
  const mounted = useRef(true)
  const requestPending = useRef(false)
  const sendPending = useRef(false)
  const countdown = useRef(0)
  const phoneValid = phoneReg.test(phone)
  const canContinue = phoneValid && /^\d{6}$/.test(verification)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    const update = () => {
      const remaining = Math.max(0, Math.ceil((retryAt - Date.now()) / 1000))
      setSeconds(remaining)
      if (!remaining) window.clearInterval(countdown.current)
    }
    update()
    if (retryAt > Date.now()) countdown.current = window.setInterval(update, 1000)
    return () => window.clearInterval(countdown.current)
  }, [retryAt])

  const sendCode = async () => {
    if (!phoneValid || sendPending.current || requestPending.current || retryAt > Date.now()) return
    sendPending.current = true
    setSending(true)
    setError('')
    try {
      const encryptedPhone = encryptStr(phone)
      if (!encryptedPhone) throw new Error('加密失败，请刷新页面重试')
      await sendVerifyCode({ phone: encryptedPhone })
      if (!mounted.current) return
      onCodeSent(Date.now() + 60000)
    } catch (cause: any) {
      if (!mounted.current) return
      const code = cause?.response?.data?.code as ERROR_CODE | undefined
      setError(code === ERROR_CODE.USER_NOT_FOUND
        ? '无法设置密码，账号无权限，请联系学校管理员'
        : (code && ERROR_CODE_ZH[code]) || cause?.response?.data?.message || cause?.message || '获取验证码失败，请稍后重试')
    } finally {
      sendPending.current = false
      if (mounted.current) setSending(false)
    }
  }

  const next = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canContinue || requestPending.current || sendPending.current) return
    requestPending.current = true
    setBusy(true)
    setError('')
    try {
      await onNext(phone, verification)
    } catch (cause: any) {
      if (mounted.current) setError(cause?.response?.data?.message || cause?.message || '验证失败，请稍后重试')
    } finally {
      requestPending.current = false
      if (mounted.current) setBusy(false)
    }
  }

  return (
    <form className={lessModule.card} onSubmit={next} aria-labelledby="verify-phone-title">
      <h1 id="verify-phone-title">验证手机号</h1>
      <p className={lessModule.description}>完成账号安全验证后，可设置登录密码</p>
      <Input
        className={lessModule.input}
        aria-label="手机号"
        aria-describedby="verify-phone-error"
        inputMode="tel"
        autoComplete="tel"
        placeholder="请输入手机号"
        value={formatPhone(phone)}
        disabled={busy || sending}
        onChange={(event) => {
          setPhone(event.target.value.replace(/\D/g, '').slice(0, 11))
          setVerification('')
          setError('')
        }}
        onBlur={() => setError(phone && !phoneValid ? '手机号格式错误' : '')}
      />
      <Input
        className={`${lessModule.input} ${lessModule.codeInput}`}
        aria-label="验证码"
        aria-describedby="verify-phone-error"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="请输入验证码"
        value={verification}
        disabled={busy}
        onChange={(event) => {
          setVerification(event.target.value.replace(/\D/g, '').slice(0, 6))
          setError('')
        }}
        onBlur={() => { if (verification && !/^\d{6}$/.test(verification)) setError('验证码为6位数字') }}
        suffix={(
          <Button
            type="text"
            htmlType="button"
            className={lessModule.sendCode}
            disabled={!phoneValid || busy || sending || seconds > 0}
            onClick={sendCode}
          >
            {sending ? '发送中...' : seconds ? `${seconds} 秒后重试` : '获取验证码'}
          </Button>
        )}
      />
      <div className={lessModule.error} id="verify-phone-error" aria-live="polite">{error}</div>
      <div className={lessModule.actions}>
        <Button type="bordered" htmlType="button" className={lessModule.cancel} disabled={busy} onClick={onCancel}>取消</Button>
        <Button type="primary" htmlType="submit" className={lessModule.submit} disabled={!canContinue || busy || sending}>
          {busy ? '验证中...' : '下一步'}
        </Button>
      </div>
    </form>
  )
}
